import { isMainThread } from 'node:worker_threads';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { SymbolIndexDeferredError } from '../../code-nav/symbol-index/deferred-indexing.js';
import { BROWSE_INDEX_COOLDOWN_MS } from './activity-types.js';
import {
  currentFileBrowserActivity,
  getFileBrowserInFlightReadCount,
} from './activity-tracker.js';

/** True while browse/read is active or browse recently finished — pause background indexing. */
export function isBridgeBrowseInteractiveActive(): boolean {
  const { inFlightRequests, inFlightReads, lastActivityAt } = currentFileBrowserActivity();
  if (inFlightRequests > 0) return true;
  if (inFlightReads > 0) return true;
  if (lastActivityAt === 0) return false;
  return Date.now() - lastActivityAt < BROWSE_INDEX_COOLDOWN_MS;
}

/** Throw when file browser work should preempt background indexing (e.g. before sync tree-sitter). */
export function assertBackgroundIndexingAllowed(): void {
  if (isBridgeBrowseInteractiveActive()) throw new SymbolIndexDeferredError();
}

/** Wait until no file reads are in flight. */
export async function yieldUntilFileBrowserReadsIdle(pollMs = 5): Promise<void> {
  while (getFileBrowserInFlightReadCount() > 0) {
    await yieldToEventLoop();
    if (getFileBrowserInFlightReadCount() > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
    }
  }
}

/** Wait until browse requests finish and browse cooldown has elapsed (ignores in-flight reads). */
export async function yieldUntilFileBrowserBrowseIdle(pollMs = 5): Promise<void> {
  while (true) {
    const { inFlightRequests, lastActivityAt } = currentFileBrowserActivity();
    const browseCooldownDone =
      lastActivityAt === 0 || Date.now() - lastActivityAt >= BROWSE_INDEX_COOLDOWN_MS;
    if (inFlightRequests === 0 && browseCooldownDone) return;
    await yieldToEventLoop();
    await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
  }
}

/** @deprecated Prefer {@link yieldUntilFileBrowserBrowseIdle} or {@link yieldUntilBackgroundIndexingAllowed}. */
export async function yieldUntilFileBrowserIdle(pollMs = 5): Promise<void> {
  await yieldUntilBackgroundIndexingAllowed(pollMs);
}

/** Wait until reads, browse, and browse cooldown allow background indexing/file walks. */
export async function yieldUntilBackgroundIndexingAllowed(pollMs = 5): Promise<void> {
  await yieldUntilFileBrowserReadsIdle(pollMs);
  await yieldUntilFileBrowserBrowseIdle(pollMs);
}

/** Yield to the event loop, then pause background work while file browser reads/browse are active. */
export async function yieldForBridgeInteractivePriority(): Promise<void> {
  await yieldToEventLoop();
  if (isMainThread) {
    await yieldUntilBackgroundIndexingAllowed();
    return;
  }
  assertBackgroundIndexingAllowed();
}
